import { ChangeEvent, FC, MouseEvent, useEffect, useState } from 'react';

import { Buttons, Form, FormElements } from '../../../common/AddEditFormLayout/AddEditFormLayout.styles';
import {
  DropdownWrapperMedium,
  InputWrapperLarge,
  InputWrapperMedium
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import { RadioWrapper, RowWrapper } from './EditSize.styles';

import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { useEditSize, useSize } from '../../../services/size/size.service';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';

import { NumberFormatValues, SourceInfo } from 'react-number-format';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import { STATUS, StatusKeys } from '../../../constants/ApiEnums';
import {OPTIONS_PLACEHOLDER} from "../../../constants/Dropdown";
import { SubTitle } from '../../Classes/ClassForm/ClassForm.styles';
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';

const EditSize: FC = () => {
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();
  const { sizeNo } = useParams<{ sizeNo: string }>();
  const _sizeNo = sizeNo as string;

  const { data, isError } = useSize({ sizeNo: _sizeNo }, true);
  const { mutate, isError: editSizeIsError, isLoading, error: editSizeError } = useEditSize(_sizeNo);

  useFetchErrorToast([{ isError, label: 'Error fetching size data', id: 'sizeDataFetchError' }]);
  useGenericErrorMessage([{ error: editSizeError, isError: editSizeIsError }]);

  const sizeData = data?.data;

  const [formValues, setFormValues] = useState({
    displayInOe: false,
    validOnWeb: false,
    displayDesc: '',
    seqNo: '',
    statusNo: '',
  });

  const [initialFormValues, setInitialFormValues] = useState({
    displayInOe: false,
    validOnWeb: false,
    displayDesc: '',
    seqNo: '',
    statusNo: '',
  });

  const [formErrors, setFormErrors] = useState({
    displayDesc: '',
    seqNo: '',
    statusNo: '',
  });

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const checkForUnsavedChanges = () => {
    if (
      initialFormValues.displayInOe === formValues.displayInOe &&
      initialFormValues.validOnWeb === formValues.validOnWeb &&
      initialFormValues.displayDesc === formValues.displayDesc && 
      initialFormValues.seqNo === formValues.seqNo &&
      initialFormValues.statusNo === formValues.statusNo
    ) {
      setUnsavedChanges(false);
    } else {
      setUnsavedChanges(true);
    }
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues]);

  useEffect(() => {
    if (sizeData) {
      setFormValues({
        displayInOe: sizeData.displayInOe,
        validOnWeb: sizeData.validOnWeb,
        displayDesc: sizeData.displayDesc,
        seqNo: sizeData.seqNo.toString(),
        statusNo: sizeData.statusNo.toString()
      });
      setInitialFormValues({
        displayInOe: sizeData.displayInOe,
        validOnWeb: sizeData.validOnWeb,
        displayDesc: sizeData.displayDesc,
        seqNo: sizeData.seqNo.toString(),
        statusNo: sizeData.statusNo.toString()
      });
    }
  }, [sizeData]);

  const handleDisplayInOeChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, displayInOe: event.target.id === 'YdisplayInOe' });
  };

  const handleValidonWebChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, validOnWeb: event.target.id === 'YvalidOnWeb' });
  };

  const handleStatusDropdown = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
    setFormErrors && setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleBohDescChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormErrors({
      ...formErrors,
      displayDesc: '',
    });
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleMaskedInputChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.event) {
      setFormValues({ ...formValues, [sourceInfo.event.target.id]: values.value === '' ? '' : values.value });
      setFormErrors({ ...formErrors, [sourceInfo.event.target.id]: '' });
    }
  };


  const handleSave = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    let error = false;
    let newFormErrors = {
      displayDesc: '',
      seqNo: '',
      statusNo: '',
    };

    if (formValues.displayDesc?.length > 9) {
      error = true;
      newFormErrors.displayDesc = 'BOH Description must be 9 characters or less';
    }

    if (!formValues.displayDesc || formValues.displayDesc.length < 1) {
      error = true;
      newFormErrors.displayDesc = 'Please enter a BOH Description';
    }

    if ([null, undefined, -1, ''].includes(formValues.seqNo)) {
      error = true;
      newFormErrors.seqNo = 'Sequence Number is required.';
    }

    if(formValues.seqNo?.length > 3) {
      error = true;
      newFormErrors.seqNo = 'Sequence Number must be 3 characters or less'
    }

    if (!error) {
      setUnsavedChanges(false);
      mutate(
        {
          sizeNo: _sizeNo,
          displayInOe: formValues.displayInOe,
          validOnWeb: formValues.validOnWeb,
          displayDesc: formValues.displayDesc,
          seqNo: +formValues.seqNo,
          statusNo: +formValues.statusNo,

        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['size', _sizeNo]);
            navigateWithToast(`/tables/sizes`, {
              label: 'Size has been updated successfully',
              id: 'sizeUpdateSuccess',
            });
          },
        }
      );
    } else {
      setFormErrors(newFormErrors);
    }
  };

  return (
    <>
      <Header
        breadcrumb={{ label: 'Back to Table', to: '/tables/sizes' }}
        title={`View Size ${sizeData?.sizeNo ? `- ${sizeData?.sizeNo}` : ''}`}
      />
      <Form>
        <FormElements>
          <H2>Information</H2>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='MOA Size Number' id='moaSizeNo' value={sizeData?.sizeNo?.toString()} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <Input label='Size Number' id='sizeNo' value={sizeData?.sizeNo?.toString()} disabled />
            </InputWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='Short Description' id='description' value={sizeData?.description} disabled />
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Input label='Long Description' id='longDescription' value={sizeData?.longDescription} disabled />
            </InputWrapperLarge>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperLarge>
              <Input
                label='BOH Description'
                id='displayDesc'
                value={formValues?.displayDesc}
                onChange={handleBohDescChange}
                errorText={formErrors.displayDesc}
              />
            </InputWrapperLarge>
            <DropdownWrapperMedium>
              <Dropdown
                label='Status'
                id='statusNo'
                options={Object.keys(STATUS).map((key: any) => {
                  return { value: key, label: `${key} - ${STATUS[key as StatusKeys]}` };
                })}
                value={formValues?.statusNo?.toString() || OPTIONS_PLACEHOLDER}
                errorText={formErrors?.statusNo}
                onChange={handleStatusDropdown}
              />
            </DropdownWrapperMedium>
          </RowWrapper>
          <RowWrapper>
            <InputWrapperMedium>
              <Input label='Mnemonic' id='mnemonic' value={sizeData?.mnemonic} disabled />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                label='Sequence Number'
                id='seqNo'
                value={formValues?.seqNo.toString()}
                errorText={formErrors?.seqNo}
                isAllowed={(values) => +values.value < 2147483648}
                onValueChange={handleMaskedInputChange}
              />
            </InputWrapperMedium>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'YvalidOnWeb',
                    label: 'Yes',
                  },
                  {
                    id: 'NvalidOnWeb',
                    label: 'No',
                  },
                ]}
                id='validOnWeb'
                label='Valid on Web?'
                layout={RadioGroupLayouts.Row}
                selected={sizeData?.validOnWeb ? 'YvalidOnWeb' : 'NvalidOnWeb'}
                onChange={handleValidonWebChange}
              />
            </RadioWrapper>
            <RadioWrapper>
              <RadioGroup
                options={[
                  {
                    id: 'YdisplayInOe',
                    label: 'Yes',
                  },
                  {
                    id: 'NdisplayInOe',
                    label: 'No',
                  },
                ]}
                id='displayInOe'
                label='Valid in Order Entry?'
                layout={RadioGroupLayouts.Row}
                selected={formValues?.displayInOe ? 'YdisplayInOe' : 'NdisplayInOe'}
                onChange={handleDisplayInOeChange}
              />
            </RadioWrapper>
          </RowWrapper>
        </FormElements>
        <Buttons>
          <Button label='Save' onClick={handleSave} disabled={!unsavedChanges} isLoading={isLoading} />
        </Buttons>
      </Form>
      <SubTitle>{readOnlyLabelTableText}</SubTitle>
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default EditSize;
